<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Popover from '@uiKit/elements/Popover.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { BuildingIcon } from '@uiKit/icons'
import { formatDayMonthYear, formatDaysAgo, formatMoney } from '@shared/utils'
import ListingStageStatus from './ListingStageStatus.vue'
import { propertyTypeLabelOf } from '../../constants/listingRequest'
import { DRAFT_STATUS } from '../../constants/listingRequestLifecycle'
import { statusChipOf } from '../../constants/listingRequestStatusChips'
import type { ListingRequestSummary } from '../../types/listingRequestTypes'

// One row of the Sell Request table. Presentational — emits `view` with the id.
// The column template comes from the owning table (`--list-table-columns`) so the
// header and every row share one definition; below 992px the same cells regroup
// into a card, each value gaining the label the hidden header used to supply.
const props = defineProps<{ request: ListingRequestSummary }>()
const emit = defineEmits<{ (e: 'view' | 'discard', id: string): void }>()

// A draft opens back into the wizard (the detail route redirects it there) and
// can be thrown away; a submitted request can only be viewed.
const isDraft = computed(() => props.request.requestStatus === DRAFT_STATUS)

const title = computed(() => props.request.propertyName ?? 'Untitled property')
const statusChip = computed(() => statusChipOf(props.request))
const propertyType = computed(() => propertyTypeLabelOf(props.request.propertyType))

const toAmount = (value: string | null): number | null => {
  if (value === null || value.trim() === '') return null
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : null
}

const askingPrice = computed(() => {
  const amount = toAmount(props.request.askingPrice)
  return amount === null ? '—' : formatMoney(props.request.currencyCode, amount)
})

// Null until a price negotiation closes, so most early-stage rows show a dash.
const agreedPrice = computed(() => {
  const amount = toAmount(props.request.priceAgreed)
  return amount === null ? '—' : formatMoney(props.request.currencyCode, amount)
})

// What the seller takes home, once the list endpoint sends it.
const priceNote = computed(() => {
  const net = toAmount(props.request.netSettlementAmount)
  return net === null ? '' : `Net ${formatMoney(props.request.currencyCode, net)}`
})

// A draft has no submit date, so it shows when it was started instead — under a
// label that says so, rather than passing the creation date off as a submit.
const dateLabel = computed(() => (isDraft.value ? 'Started' : 'Submitted'))
const submittedIso = computed(() => props.request.submittedAt ?? props.request.createdAt)
const submittedOn = computed(() => formatDayMonthYear(submittedIso.value))
const submittedAgo = computed(() => formatDaysAgo(submittedIso.value))

function view() {
  emit('view', props.request.id)
}

// The ⋮ opens a small actions menu rather than navigating straight away; the
// row itself stays a one-click shortcut to the detail page.
const actionsMenu = ref<InstanceType<typeof Popover> | null>(null)
const isMenuOpen = ref(false)

function toggleMenu(event: MouseEvent) {
  actionsMenu.value?.toggle(event)
}

function viewFromMenu() {
  actionsMenu.value?.hide()
  view()
}

function discardFromMenu() {
  actionsMenu.value?.hide()
  emit('discard', props.request.id)
}
</script>

<template>
  <Box is="li" class="request-row" @click="view">
    <Box flex align="center" gap="3" class="request-row__cell request-row__cell--property">
      <Box flex align="center" justify="center" class="request-row__icon" aria-hidden="true">
        <BuildingIcon width="20" height="20" />
      </Box>
      <Box direction="col" gap="1" class="request-row__identity">
        <Text is="h3" type="body" weight="semibold" truncate>{{ title }}</Text>
        <Box
          v-if="propertyType || request.requestRefCode"
          flex
          align="center"
          gap="2"
          class="request-row__meta"
        >
          <Text v-if="propertyType" is="span" type="caption-sm" class="request-row__type">
            {{ propertyType }}
          </Text>
          <Text
            v-if="request.requestRefCode"
            is="span"
            type="caption"
            color="var(--p-sliceal-text-subtle)"
            truncate
          >
            {{ request.requestRefCode }}
          </Text>
        </Box>
      </Box>
    </Box>

    <Box direction="col" gap="1" class="request-row__cell request-row__cell--location">
      <Text type="micro" class="request-row__label">Location</Text>
      <Text type="body-sm" weight="medium" truncate>{{ request.buildingName ?? '—' }}</Text>
      <Text v-if="request.locationArea" type="caption" color="muted" truncate>
        {{ request.locationArea }}
      </Text>
    </Box>

    <Box direction="col" gap="1" class="request-row__cell request-row__cell--price">
      <Text type="micro" class="request-row__label">Asking price</Text>
      <Text type="body" weight="semibold" class="request-row__nowrap">{{ askingPrice }}</Text>
      <Text v-if="priceNote" type="caption" color="muted" truncate>{{ priceNote }}</Text>
    </Box>

    <Box direction="col" gap="1" class="request-row__cell request-row__cell--agreed">
      <Text type="micro" class="request-row__label">Agreed price</Text>
      <Text type="body" weight="semibold" class="request-row__nowrap">{{ agreedPrice }}</Text>
    </Box>

    <Box direction="col" gap="1" class="request-row__cell request-row__cell--status">
      <Text type="micro" class="request-row__label">Status</Text>
      <ListingStageStatus
        :chip="statusChip"
        :state="request.stageState"
        :current-step="request.stageStep"
      />
    </Box>

    <Box direction="col" gap="1" class="request-row__cell request-row__cell--submitted">
      <Text type="micro" class="request-row__label">{{ dateLabel }}</Text>
      <Text type="body-sm" weight="medium" class="request-row__nowrap">{{ submittedOn }}</Text>
      <Text v-if="submittedAgo" type="caption" color="var(--p-sliceal-text-subtle)">
        {{ submittedAgo }}
      </Text>
    </Box>

    <Box flex justify="end" class="request-row__cell request-row__cell--action">
      <Button
        variant="text"
        color="secondary"
        icon="ellipsis-v"
        size="small"
        class="request-row__action"
        :aria-label="`More actions for ${title}`"
        aria-haspopup="true"
        :aria-expanded="isMenuOpen"
        @click.stop="toggleMenu"
      />
      <Popover ref="actionsMenu" @show="isMenuOpen = true" @hide="isMenuOpen = false">
        <Box direction="col" class="request-row__menu" role="menu">
          <button type="button" role="menuitem" class="request-row__menu-item" @click="viewFromMenu">
            <PiIcon :name="isDraft ? 'pen-to-square' : 'eye'" class="request-row__menu-icon" />
            <Text is="span" type="body-sm">{{ isDraft ? 'Continue editing' : 'View details' }}</Text>
          </button>
          <button
            v-if="isDraft"
            type="button"
            role="menuitem"
            class="request-row__menu-item request-row__menu-item--danger"
            @click="discardFromMenu"
          >
            <PiIcon name="times-circle" class="request-row__menu-icon" />
            <Text is="span" type="body-sm" color="inherit">Discard draft</Text>
          </button>
        </Box>
      </Popover>
    </Box>
  </Box>
</template>

<style scoped>
/* Anchors the hidden cell labels here — unanchored, they stretch the page. */
.request-row {
  position: relative;
  display: grid;
  grid-template-columns: var(--list-table-columns);
  align-items: center;
  column-gap: 1rem;
  padding: 0.9375rem 1.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.request-row:last-child {
  border-bottom: none;
}

.request-row:hover {
  background: var(--p-surface-50);
}

.request-row__cell {
  min-width: 0;
}

/* The design sets every cell at the font's natural line height (71px rows);
   the kit's body presets carry paragraph leading, which would pad each row. */
.request-row {
  line-height: normal;
}

.request-row :deep(.text--body),
.request-row :deep(.text--body-sm) {
  line-height: normal;
}

.request-row__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--p-primary-50);
  color: var(--p-primary-500);
}

.request-row__identity {
  min-width: 0;
}

.request-row__meta {
  min-width: 0;
}

.request-row__type {
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--p-surface-100);
  color: var(--p-surface-600);
  letter-spacing: normal;
}

.request-row__nowrap {
  white-space: nowrap;
}

/* On the table the (aria-hidden) header row names each column visually, so the
   per-cell labels are visually hidden but still read aloud; the stacked card
   below 992px shows them. */
.request-row__label {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.request-row__action {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

/* Mirrors admin-web's DataTableV2 row menu: compact rows, small icon, tinted
   hover. The popover is teleported, but slot content keeps this scope id. */
.request-row__menu {
  padding: 0.25rem;
  min-width: 180px;
}

.request-row__menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  border: none;
  border-radius: var(--p-border-radius-sm);
  background: transparent;
  color: var(--p-text-color);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.request-row__menu-item:hover,
.request-row__menu-item:focus-visible {
  background: var(--p-surface-100);
  outline: none;
}

.request-row__menu-item--danger {
  color: var(--p-danger-600);
}

.request-row__menu-item--danger:hover,
.request-row__menu-item--danger:focus-visible {
  background: var(--p-danger-50);
}

.request-row__menu-icon {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.request-row__menu-item--danger .request-row__menu-icon {
  color: inherit;
}

@media (max-width: 992px) {
  .request-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      'property property'
      'location price'
      'agreed submitted'
      'status status';
    row-gap: 1rem;
    padding: 1rem;
  }

  .request-row__cell--property {
    grid-area: property;
    /* Clears the chevron pinned to the card's top-right corner. */
    padding-right: 3rem;
  }

  .request-row__cell--location {
    grid-area: location;
  }

  .request-row__cell--price {
    grid-area: price;
  }

  .request-row__cell--agreed {
    grid-area: agreed;
  }

  .request-row__cell--status {
    grid-area: status;
  }

  .request-row__cell--submitted {
    grid-area: submitted;
  }

  .request-row__cell--action {
    position: absolute;
    top: 0.75rem;
    right: 0.5rem;
  }

  .request-row__label {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip-path: none;
  }

  .request-row__action {
    width: 44px;
    height: 44px;
  }

  .request-row__nowrap {
    white-space: normal;
  }
}
</style>
